/**
 * Msg 590 · Phase A — Admin Categories screen.
 *
 * User-defined categories replace the retired hard-coded `entity_type`
 * enum. Each category has a name + a lucide icon; folders reference
 * categories by id via `atlas_topics.category_id`.
 *
 * Delete is SAFE-BY-DESIGN: the endpoint only sets `category_id=null`
 * on every folder referencing the category and removes the category
 * doc itself. No topic docs are ever touched.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Trash2, Loader2, X as CloseIcon, Search, Check, AlertCircle,
  ArrowLeft, Palette, Pencil,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CURATED_ICONS, iconByName } from "../topicIconMap";
import {
  useAtlasCategories, invalidateCategories,
} from "../atlasCategoriesCache";


// ─────────────────────────────────────────────────────────────
// Icon picker — searchable grid.
// ─────────────────────────────────────────────────────────────
function IconPicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const qn = q.trim().toLowerCase();
    if (!qn) return CURATED_ICONS;
    return CURATED_ICONS.filter((i) => i.name.toLowerCase().includes(qn));
  }, [q]);
  return (
    <div className="space-y-2" data-testid="atlas-categories-icon-picker">
      <div className="flex items-center gap-2 rounded-lg border border-tb-navy/20 px-2 py-1">
        <Search className="w-3.5 h-3.5 text-tb-navy/50" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search icon — Ship, Palmtree, Mountain…"
          data-testid="atlas-categories-icon-picker-search"
          className="flex-1 outline-none text-xs bg-transparent text-tb-navy placeholder:text-tb-navy/40"
        />
      </div>
      <div className="grid grid-cols-8 gap-1.5 max-h-[220px] overflow-y-auto pr-1">
        {filtered.map(({ name, cmp: Icon }) => {
          const active = value === name;
          return (
            <button
              key={name}
              type="button"
              onClick={() => onChange(name)}
              title={name}
              data-testid={`atlas-categories-icon-option-${name}`}
              className={"aspect-square rounded-lg flex items-center justify-center transition "
                + (active
                    ? "bg-tb-navy text-tb-gold-bright"
                    : "bg-tb-navy/[0.04] hover:bg-tb-navy/10 text-tb-navy/70")}
            >
              <Icon className="w-4 h-4" />
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-8 text-center text-xs font-hand text-tb-navy/50 py-4">
            No matches.
          </div>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// New-category card.
// ─────────────────────────────────────────────────────────────
function AddCategoryCard({ onCreated }) {
  const [name, setName] = useState("");
  const [iconName, setIconName] = useState("Anchor");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const r = await api.post("/atlas/admin/categories", {
        name: trimmed, icon_name: iconName,
      });
      toast.success(`Created "${r.data.category.name}".`);
      setName("");
      setIconName("Anchor");
      await invalidateCategories();
      onCreated?.(r.data.category);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed.");
    } finally {
      setBusy(false);
    }
  };
  const Preview = iconByName(iconName);
  return (
    <div className="rounded-2xl border border-tb-navy/15 bg-white p-4 space-y-3"
         data-testid="atlas-categories-add-card">
      <div className="text-[10px] font-hand uppercase tracking-widest text-tb-navy/50">
        Add a new category
      </div>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-tb-navy/[0.06] flex items-center justify-center"
             data-testid="atlas-categories-add-preview">
          <Preview className="w-5 h-5 text-tb-navy" />
        </div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Category name — e.g. Cruise Line, Resort, Airport"
          data-testid="atlas-categories-add-name"
          className="flex-1 h-10 rounded-lg border border-tb-navy/20 px-3 text-sm text-tb-navy outline-none focus:border-tb-navy"
        />
      </div>
      <IconPicker value={iconName} onChange={setIconName} />
      <button
        type="button"
        onClick={submit}
        disabled={busy || !name.trim()}
        data-testid="atlas-categories-add-submit"
        className="w-full inline-flex items-center justify-center gap-1.5 h-10 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-40"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        Add category
      </button>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Edit-in-place row.
// ─────────────────────────────────────────────────────────────
function CategoryRow({ category, onChanged, onDeleteRequest }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(category.name);
  const [iconName, setIconName] = useState(category.icon_name || "Folder");
  const [busy, setBusy] = useState(false);
  const Icon = iconByName(editing ? iconName : category.icon_name);
  useEffect(() => {
    setName(category.name);
    setIconName(category.icon_name || "Folder");
  }, [category.name, category.icon_name]);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/atlas/admin/categories/${category.id}`, {
        name: name.trim(), icon_name: iconName,
      });
      toast.success("Category updated.");
      await invalidateCategories();
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      data-testid={`atlas-categories-row-${category.id}`}
      className="rounded-xl border border-tb-navy/10 bg-white p-3 flex items-start gap-3"
    >
      <div className="w-10 h-10 rounded-full bg-tb-navy/[0.06] flex items-center justify-center shrink-0">
        <Icon className="w-4.5 h-4.5 text-tb-navy" />
      </div>
      <div className="flex-1 min-w-0">
        {!editing ? (
          <>
            <div className="font-medium text-tb-navy truncate"
                 data-testid={`atlas-categories-name-${category.id}`}>
              {category.name}
            </div>
            <div className="text-[11px] font-hand text-tb-navy/50">
              {category.icon_name} · {category.folder_count ?? 0} folder{category.folder_count === 1 ? "" : "s"}
            </div>
          </>
        ) : (
          <div className="space-y-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid={`atlas-categories-edit-name-${category.id}`}
              className="w-full h-9 rounded-lg border border-tb-navy/20 px-3 text-sm text-tb-navy outline-none focus:border-tb-navy"
            />
            <IconPicker value={iconName} onChange={setIconName} />
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!editing ? (
          <>
            <button
              type="button"
              onClick={() => setEditing(true)}
              aria-label="Edit"
              data-testid={`atlas-categories-edit-${category.id}`}
              className="w-8 h-8 inline-flex items-center justify-center rounded-full hover:bg-tb-navy/5 text-tb-navy/70"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onDeleteRequest?.(category)}
              aria-label="Delete"
              data-testid={`atlas-categories-delete-${category.id}`}
              className="w-8 h-8 inline-flex items-center justify-center rounded-full hover:bg-red-50 text-tb-navy/70 hover:text-red-600"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              aria-label="Save"
              data-testid={`atlas-categories-save-${category.id}`}
              className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-tb-navy text-tb-gold-bright disabled:opacity-40"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setName(category.name); setIconName(category.icon_name); }}
              aria-label="Cancel"
              data-testid={`atlas-categories-cancel-${category.id}`}
              className="w-8 h-8 inline-flex items-center justify-center rounded-full hover:bg-tb-navy/5 text-tb-navy/70"
            >
              <CloseIcon className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Delete-confirm modal — makes the safety promise explicit.
// ─────────────────────────────────────────────────────────────
function DeleteCategoryModal({ category, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  if (!category) return null;
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/atlas/admin/categories/${category.id}`);
      toast.success(`Deleted "${category.name}". ${r.data?.untagged_topics ?? 0} folder(s) reset to no category. No folders deleted.`);
      await invalidateCategories();
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
         data-testid="atlas-categories-delete-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-tb-navy/15">
        <div className="px-6 pt-5 pb-3 border-b border-tb-navy/10">
          <h2 className="text-tb-navy text-xl font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            Delete "{category.name}"?
          </h2>
        </div>
        <div className="p-6 space-y-3 text-sm text-tb-navy font-hand">
          <p>
            This will remove the <b>{category.name}</b> category. Every folder currently tagged with it
            (<b>{category.folder_count ?? 0}</b>) will be un-tagged and their icon will fall back to the generic
            folder icon.
          </p>
          <p className="rounded-lg bg-tb-navy/[0.04] p-3 text-tb-navy/70">
            <b>No folders will be deleted.</b> Only the category tag is removed.
          </p>
        </div>
        <div className="px-6 pb-5 pt-1 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
                  data-testid="atlas-categories-delete-cancel"
                  className="h-10 px-4 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-sm hover:bg-tb-navy/5">
            Cancel
          </button>
          <button type="button" onClick={confirm} disabled={busy}
                  data-testid="atlas-categories-delete-confirm"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-red-700 text-white font-hand text-sm disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete category
          </button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Main page.
// ─────────────────────────────────────────────────────────────
export default function AtlasCategoriesPage() {
  const categories = useAtlasCategories();
  const [pendingDelete, setPendingDelete] = useState(null);
  return (
    <div className="p-6 md:p-10 max-w-5xl" data-testid="atlas-categories-page">
      <div className="mb-2">
        <Link to="/atlas/topics"
              data-testid="atlas-categories-back"
              className="inline-flex items-center gap-1 text-tb-navy/60 hover:text-tb-navy text-xs font-hand">
          <ArrowLeft className="w-3 h-3" /> Back to Topics
        </Link>
      </div>
      <div className="mb-4">
        <h1 className="text-tb-navy text-3xl md:text-4xl font-semibold flex items-center gap-3"
            style={{ fontFamily: '"Playfair Display", serif' }}>
          <Palette className="w-7 h-7 text-tb-gold-bright" />
          Categories
        </h1>
        <p className="mt-1 font-hand text-tb-navy/60 text-sm max-w-2xl">
          Create the categories advisors pick from when tagging a folder. Each category
          controls the folder's left-rail icon. Deleting a category never deletes any folder.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-tb-navy text-lg font-semibold"
                style={{ fontFamily: '"Playfair Display", serif' }}>
              All categories
            </h2>
            <span className="text-[10px] font-hand uppercase tracking-widest text-tb-navy/50">
              {categories.length}
            </span>
          </div>
          {categories.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-tb-navy/20 bg-tb-navy/[0.02] p-6 text-center"
                 data-testid="atlas-categories-empty">
              <AlertCircle className="w-5 h-5 text-tb-navy/40 mx-auto mb-2" />
              <p className="text-sm font-hand text-tb-navy/60">
                No categories yet. Add your first one on the right.
              </p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="atlas-categories-list">
              {categories.map((c) => (
                <CategoryRow
                  key={c.id}
                  category={c}
                  onDeleteRequest={setPendingDelete}
                />
              ))}
            </div>
          )}
        </div>

        <div>
          <AddCategoryCard />
        </div>
      </div>

      <DeleteCategoryModal
        category={pendingDelete}
        onClose={() => setPendingDelete(null)}
        onDeleted={() => setPendingDelete(null)}
      />
    </div>
  );
}
